<script lang="ts">
  import { onMount } from 'svelte';
  import type { CommandSetSummary, DeviceProfileSummary, DeviceSkuSummary, FirmwareCheckinEntry, SkuFirmwareInfo } from '@ucc/shared';
  import {
    ApiError,
    createDeviceSku,
    deleteDeviceSku,
    fetchCommandSets,
    fetchDeviceProfiles,
    fetchDeviceSkus,
    fetchSkuFirmware,
    fetchSkuFirmwareCheckins,
    setSkuCommandSet,
    updateDeviceSku,
    uploadSkuFirmware,
  } from '$lib/api';
  import { session } from '$lib/session.svelte';
  import Button from '$lib/components/Button.svelte';
  import Card from '$lib/components/Card.svelte';

  let skus = $state<DeviceSkuSummary[]>([]);
  let profileOptions = $state<DeviceProfileSummary[]>([]);
  let commandSetOptions = $state<CommandSetSummary[]>([]);
  let loading = $state(true);
  let error = $state<string | null>(null);
  let formError = $state<string | null>(null);
  let saving = $state(false);

  let newCode = $state('');
  let newLabel = $state('');
  let newProfileIds = $state<string[]>([]);
  let newDefaultId = $state('');
  let newCommandSetId = $state('');

  let editId = $state<string | null>(null);
  let editCode = $state('');
  let editLabel = $state('');
  let editProfileIds = $state<string[]>([]);
  let editDefaultId = $state('');
  let editCommandSetId = $state('');

  // Firmware OTA (2026-09-15) — a SKU's own `code` doubles as the OTA "folder".
  let firmwareOpenId = $state<string | null>(null);
  let firmwareInfo = $state<SkuFirmwareInfo | null>(null);
  let firmwareCheckins = $state<FirmwareCheckinEntry[]>([]);
  let firmwareLoading = $state(false);
  let firmwareUploading = $state(false);
  let firmwareError = $state<string | null>(null);
  let firmwarePickedFile = $state<File | null>(null);

  const role = $derived(session.user?.role);
  const isStaff = $derived(role === 'admin' || role === 'user');

  onMount(() => {
    void reload();
  });

  async function reload() {
    loading = true;
    error = null;
    try {
      const [skuRes, profRes, csRes] = await Promise.all([
        fetchDeviceSkus({ all: true }),
        fetchDeviceProfiles(),
        fetchCommandSets().catch(() => ({ commandSets: [] as CommandSetSummary[] })),
      ]);
      skus = skuRes.skus ?? [];
      profileOptions = profRes.profiles ?? [];
      commandSetOptions = csRes.commandSets ?? [];
    } catch (err) {
      error = err instanceof ApiError ? err.message : 'Failed to load SKUs';
      skus = [];
    } finally {
      loading = false;
    }
  }

  function profileLabel(p: { meterModel: string; profileId: number; profileVersion?: number }): string {
    const v = p.profileVersion != null ? ` v${p.profileVersion}` : '';
    return `${p.meterModel} · pid ${p.profileId}${v}`;
  }

  function commandSetName(id: string | null | undefined): string | null {
    if (!id) return null;
    return commandSetOptions.find((c) => c.id === id)?.name ?? null;
  }

  function toggleNewProfile(id: string) {
    if (newProfileIds.includes(id)) {
      newProfileIds = newProfileIds.filter((x) => x !== id);
      if (newDefaultId === id) newDefaultId = newProfileIds[0] ?? '';
    } else {
      newProfileIds = [...newProfileIds, id];
      if (!newDefaultId) newDefaultId = id;
    }
  }

  function toggleEditProfile(id: string) {
    if (editProfileIds.includes(id)) {
      editProfileIds = editProfileIds.filter((x) => x !== id);
      if (editDefaultId === id) editDefaultId = editProfileIds[0] ?? '';
    } else {
      editProfileIds = [...editProfileIds, id];
      if (!editDefaultId) editDefaultId = id;
    }
  }

  async function onCreate(e: Event) {
    e.preventDefault();
    formError = null;
    const code = newCode.trim();
    if (!code) {
      formError = 'SKU code is required.';
      return;
    }
    if (newProfileIds.length === 0) {
      formError = 'Select at least one profile.';
      return;
    }
    saving = true;
    try {
      const res = await createDeviceSku({
        code,
        label: newLabel.trim() || undefined,
        profileIds: newProfileIds,
        defaultProfileId: newDefaultId || undefined,
      });
      if (newCommandSetId) {
        await setSkuCommandSet(res.sku.id, newCommandSetId);
      }
      newCode = '';
      newLabel = '';
      newProfileIds = [];
      newDefaultId = '';
      newCommandSetId = '';
      await reload();
    } catch (err) {
      formError = err instanceof ApiError ? err.message : 'Create failed';
    } finally {
      saving = false;
    }
  }

  function startEdit(s: DeviceSkuSummary) {
    editId = s.id;
    editCode = s.code;
    editLabel = s.label ?? '';
    editProfileIds = s.profiles.map((p) => p.id);
    editDefaultId = s.profiles.find((p) => p.isDefault)?.id ?? s.profiles[0]?.id ?? '';
    editCommandSetId = s.commandSetId ?? '';
    formError = null;
  }

  function cancelEdit() {
    editId = null;
    editCode = '';
    editLabel = '';
    editProfileIds = [];
    editDefaultId = '';
    editCommandSetId = '';
  }

  async function saveEdit() {
    if (!editId) return;
    formError = null;
    if (editProfileIds.length === 0) {
      formError = 'Select at least one profile.';
      return;
    }
    saving = true;
    try {
      await updateDeviceSku(editId, {
        code: editCode.trim(),
        label: editLabel.trim() ? editLabel.trim() : null,
        profileIds: editProfileIds,
        defaultProfileId: editDefaultId || undefined,
      });
      await setSkuCommandSet(editId, editCommandSetId || null);
      cancelEdit();
      await reload();
    } catch (err) {
      formError = err instanceof ApiError ? err.message : 'Update failed';
    } finally {
      saving = false;
    }
  }

  async function toggleActive(s: DeviceSkuSummary) {
    formError = null;
    saving = true;
    try {
      await updateDeviceSku(s.id, { active: !s.active });
      await reload();
    } catch (err) {
      formError = err instanceof ApiError ? err.message : 'Update failed';
    } finally {
      saving = false;
    }
  }

  async function toggleFirmwarePanel(s: DeviceSkuSummary) {
    if (firmwareOpenId === s.id) {
      firmwareOpenId = null;
      return;
    }
    firmwareOpenId = s.id;
    firmwarePickedFile = null;
    firmwareError = null;
    await loadFirmwarePanel(s.id);
  }

  async function loadFirmwarePanel(skuId: string) {
    firmwareLoading = true;
    try {
      const [fwRes, checkinRes] = await Promise.all([
        fetchSkuFirmware(skuId),
        fetchSkuFirmwareCheckins(skuId).catch(() => ({ checkins: [] as FirmwareCheckinEntry[] })),
      ]);
      firmwareInfo = fwRes.firmware;
      firmwareCheckins = checkinRes.checkins;
    } catch (err) {
      firmwareError = err instanceof ApiError ? err.message : 'Failed to load firmware info';
    } finally {
      firmwareLoading = false;
    }
  }

  function onFirmwareFilePicked(e: Event) {
    const input = e.target as HTMLInputElement;
    firmwarePickedFile = input.files?.[0] ?? null;
  }

  async function onUploadFirmware(skuId: string) {
    if (!firmwarePickedFile) return;
    firmwareError = null;
    firmwareUploading = true;
    try {
      await uploadSkuFirmware(skuId, firmwarePickedFile);
      firmwarePickedFile = null;
      await loadFirmwarePanel(skuId);
    } catch (err) {
      firmwareError = err instanceof ApiError ? err.message : 'Upload failed';
    } finally {
      firmwareUploading = false;
    }
  }

  function formatBytes(n: number): string {
    if (n < 1024) return `${n} B`;
    if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
    return `${(n / (1024 * 1024)).toFixed(2)} MB`;
  }

  async function onDeleteSku(s: DeviceSkuSummary) {
    if (!confirm(`Delete SKU "${s.code}"? This cannot be undone.`)) return;
    formError = null;
    saving = true;
    try {
      await deleteDeviceSku(s.id);
      await reload();
    } catch (err) {
      formError = err instanceof ApiError ? err.message : 'Delete failed';
    } finally {
      saving = false;
    }
  }
</script>

<div class="page">
  <a class="back" href="#/settings">← Settings</a>
  <h2>SKU Manager</h2>
  <p class="sub">
    Maintain product SKU codes used when staff register devices. A SKU can support more than one
    meter/profile (what's actually wired on-site varies) — pick every compatible profile and mark
    one as the default for new devices. Profile files are managed under
    <a href="#/settings/profiles">Profiles</a>.
  </p>

  {#if !isStaff}
    <Card title="Staff only">
      <p class="muted">Only admin/staff can manage SKUs.</p>
    </Card>
  {:else}
    <Card title="Add SKU" subtitle="Code is unique · max 64 characters · at least one profile, one default">
      <form class="form" onsubmit={(e) => void onCreate(e)}>
        <label class="field">
          <span class="label">Code</span>
          <input
            class="input"
            type="text"
            maxlength={64}
            placeholder="e.g. UCC-GW-1"
            bind:value={newCode}
            disabled={saving}
            required
          />
        </label>
        <label class="field">
          <span class="label">Label (optional)</span>
          <input
            class="input"
            type="text"
            maxlength={120}
            placeholder="Human-readable name"
            bind:value={newLabel}
            disabled={saving}
          />
        </label>
        <fieldset class="field profiles">
          <legend class="label">Profiles (check to link, radio to mark default)</legend>
          {#if profileOptions.length === 0}
            <p class="muted">No profiles loaded yet — upload one in Profiles first.</p>
          {:else}
            <div class="check-grid">
              {#each profileOptions as p (p.id)}
                <label class="check">
                  <input
                    type="checkbox"
                    checked={newProfileIds.includes(p.id)}
                    disabled={saving}
                    onchange={() => toggleNewProfile(p.id)}
                  />
                  <span>{profileLabel(p)}</span>
                  {#if newProfileIds.includes(p.id)}
                    <label class="default-pick">
                      <input
                        type="radio"
                        name="new-default"
                        checked={newDefaultId === p.id}
                        disabled={saving}
                        onchange={() => (newDefaultId = p.id)}
                      />
                      default
                    </label>
                  {/if}
                </label>
              {/each}
            </div>
          {/if}
        </fieldset>
        <label class="field">
          <span class="label">Command Set (OTA, optional)</span>
          <select class="input" bind:value={newCommandSetId} disabled={saving}>
            <option value="">(none)</option>
            {#each commandSetOptions as cs (cs.id)}
              <option value={cs.id}>{cs.name}</option>
            {/each}
          </select>
          {#if commandSetOptions.length === 0}
            <p class="muted small">
              No Command Sets uploaded yet — see <a href="#/settings/command-sets">Command Sets</a>.
            </p>
          {/if}
        </label>
        {#if formError}
          <p class="err" role="alert">{formError}</p>
        {/if}
        <div class="actions">
          <Button type="submit" disabled={saving}>{saving ? 'Saving…' : 'Add SKU'}</Button>
        </div>
      </form>
    </Card>

    <Card title="Catalog" subtitle={loading ? 'Loading…' : `${skus.length} SKU(s)`}>
      {#if error}
        <p class="err" role="alert">{error}</p>
      {:else if loading}
        <p class="muted">Loading…</p>
      {:else if skus.length === 0}
        <p class="muted">No SKUs yet.</p>
      {:else}
        <div class="table-wrap">
          <table class="table">
            <thead>
              <tr>
                <th>Code</th>
                <th>Label</th>
                <th>Profile</th>
                <th>Command Set</th>
                <th>Status</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {#each skus as s (s.id)}
                {#if editId === s.id}
                  <tr class="editing">
                    <td colspan="6">
                      <div class="edit-row">
                        <input class="input" type="text" maxlength={64} bind:value={editCode} disabled={saving} />
                        <input
                          class="input"
                          type="text"
                          maxlength={120}
                          placeholder="Label"
                          bind:value={editLabel}
                          disabled={saving}
                        />
                        <div class="check-grid edit-profiles">
                          {#each profileOptions as p (p.id)}
                            <label class="check">
                              <input
                                type="checkbox"
                                checked={editProfileIds.includes(p.id)}
                                disabled={saving}
                                onchange={() => toggleEditProfile(p.id)}
                              />
                              <span>{profileLabel(p)}</span>
                              {#if editProfileIds.includes(p.id)}
                                <label class="default-pick">
                                  <input
                                    type="radio"
                                    name="edit-default-{s.id}"
                                    checked={editDefaultId === p.id}
                                    disabled={saving}
                                    onchange={() => (editDefaultId = p.id)}
                                  />
                                  default
                                </label>
                              {/if}
                            </label>
                          {/each}
                        </div>
                        <label class="field">
                          <span class="label">Command Set (OTA, optional)</span>
                          <select class="input" bind:value={editCommandSetId} disabled={saving}>
                            <option value="">(none)</option>
                            {#each commandSetOptions as cs (cs.id)}
                              <option value={cs.id}>{cs.name}</option>
                            {/each}
                          </select>
                        </label>
                        <div class="edit-actions">
                          <button type="button" class="linkish" disabled={saving} onclick={() => void saveEdit()}
                            >Save</button
                          >
                          <button type="button" class="linkish muted" disabled={saving} onclick={cancelEdit}
                            >Cancel</button
                          >
                        </div>
                      </div>
                    </td>
                  </tr>
                {:else}
                  <tr>
                    <td class="mono">{s.code}</td>
                    <td>{s.label ?? '—'}</td>
                    <td>
                      <span class="chips">
                        {#each s.profiles.slice(0, 3) as p}
                          <span class="chip" class:chip-default={p.isDefault} title={profileLabel(p)}
                            >{p.meterModel}#{p.profileId}{p.isDefault ? ' ★' : ''}</span
                          >
                        {/each}
                        {#if s.profiles.length > 3}
                          <span class="chip muted-chip">+{s.profiles.length - 3}</span>
                        {/if}
                        {#if s.profiles.length === 0}
                          <span class="muted">—</span>
                        {/if}
                      </span>
                    </td>
                    <td>{commandSetName(s.commandSetId) ?? '—'}</td>
                    <td>
                      <span class:off={!s.active} class="status">{s.active ? 'Active' : 'Inactive'}</span>
                      <span class="muted tiny">· {s.deviceCount} device{s.deviceCount === 1 ? '' : 's'}</span>
                    </td>
                    <td class="row-actions">
                      <button type="button" class="linkish" disabled={saving} onclick={() => startEdit(s)}
                        >Edit</button
                      >
                      <button
                        type="button"
                        class="linkish"
                        disabled={saving}
                        onclick={() => void toggleActive(s)}
                        >{s.active ? 'Deactivate' : 'Activate'}</button
                      >
                      <button
                        type="button"
                        class="linkish danger"
                        disabled={saving || s.deviceCount > 0}
                        title={s.deviceCount > 0 ? `In use by ${s.deviceCount} device(s) — cannot delete` : 'Permanently delete this SKU'}
                        onclick={() => void onDeleteSku(s)}
                        >Delete</button
                      >
                      <button type="button" class="linkish" disabled={saving} onclick={() => void toggleFirmwarePanel(s)}
                        >{firmwareOpenId === s.id ? 'Close firmware' : 'Firmware'}</button
                      >
                    </td>
                  </tr>
                  {#if firmwareOpenId === s.id}
                    <tr class="editing">
                      <td colspan="6">
                        <div class="fw-panel">
                          <p class="muted small">
                            OTA "folder" for this SKU is its own code, <strong class="mono">{s.code}</strong> — the
                            HTTPS pull URL is <span class="mono">/{s.code}?uid=&lt;device uid&gt;</span>.
                          </p>
                          {#if firmwareLoading}
                            <p class="muted">Loading…</p>
                          {:else}
                            {#if firmwareInfo}
                              <p>
                                Current: <span class="mono">{firmwareInfo.filename}</span> ·
                                {formatBytes(firmwareInfo.fileSize)} · uploaded by {firmwareInfo.uploadedByEmail ?? '—'} ·
                                {new Date(firmwareInfo.updatedAt).toLocaleString()}
                              </p>
                            {:else}
                              <p class="muted">No firmware uploaded yet for this SKU.</p>
                            {/if}
                            <div class="fw-upload">
                              <input type="file" accept=".bin" onchange={onFirmwareFilePicked} disabled={firmwareUploading} />
                              <button
                                type="button"
                                class="linkish"
                                disabled={!firmwarePickedFile || firmwareUploading}
                                onclick={() => void onUploadFirmware(s.id)}
                                >{firmwareUploading ? 'Uploading…' : firmwareInfo ? 'Replace firmware' : 'Upload firmware'}</button
                              >
                            </div>
                            {#if firmwareError}
                              <p class="err" role="alert">{firmwareError}</p>
                            {/if}
                            <p class="label" style="margin-top: 0.5rem;">Checkin log (last {firmwareCheckins.length})</p>
                            {#if firmwareCheckins.length === 0}
                              <p class="muted small">No checkins recorded yet.</p>
                            {:else}
                              <div class="table-wrap">
                                <table class="table small-table">
                                  <thead>
                                    <tr>
                                      <th>When</th>
                                      <th>UID</th>
                                      <th>Method</th>
                                      <th>Result</th>
                                      <th>Detail</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {#each firmwareCheckins as c (c.id)}
                                      <tr>
                                        <td>{new Date(c.createdAt).toLocaleString()}</td>
                                        <td class="mono">{c.uid ?? '—'}</td>
                                        <td>{c.method}</td>
                                        <td class:off={!c.success}>{c.success ? 'OK' : 'Failed'}</td>
                                        <td class="muted">
                                          {c.reason ?? (c.bytesServed != null ? `${formatBytes(c.bytesServed)} served` : '—')}
                                        </td>
                                      </tr>
                                    {/each}
                                  </tbody>
                                </table>
                              </div>
                            {/if}
                          {/if}
                        </div>
                      </td>
                    </tr>
                  {/if}
                {/if}
              {/each}
            </tbody>
          </table>
        </div>
      {/if}
    </Card>
  {/if}
</div>

<style>
  .page {
    max-width: 56rem;
    display: flex;
    flex-direction: column;
    gap: 1rem;
  }
  h2 {
    margin: 0.25rem 0 0;
    font-size: 1.5rem;
  }
  .back {
    font-size: 0.875rem;
    color: var(--text-muted);
    text-decoration: none;
  }
  .back:hover {
    color: var(--text);
  }
  .sub {
    margin: 0.35rem 0 0.5rem;
    color: var(--text-muted);
    font-size: 0.95rem;
    line-height: 1.45;
  }
  .sub a {
    color: var(--accent, #3b82f6);
  }
  .form {
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
  }
  .field {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    border: none;
    margin: 0;
    padding: 0;
  }
  .label {
    font-size: 0.8rem;
    color: var(--text-muted);
  }
  .input {
    font: inherit;
    padding: 0.5rem 0.65rem;
    border-radius: 0.5rem;
    border: 1px solid var(--border, #2a2a32);
    background: var(--surface-2, #121218);
    color: var(--text);
  }
  .actions {
    display: flex;
    gap: 0.75rem;
  }
  .err {
    color: #f87171;
    margin: 0;
    font-size: 0.9rem;
  }
  .muted {
    color: var(--text-muted);
  }
  .small {
    font-size: 0.8rem;
  }
  .small a {
    color: var(--accent, #3b82f6);
  }
  .table-wrap {
    overflow-x: auto;
  }
  .table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.9rem;
  }
  th,
  td {
    text-align: left;
    padding: 0.55rem 0.4rem;
    border-bottom: 1px solid var(--border, #2a2a32);
    vertical-align: top;
  }
  th {
    color: var(--text-muted);
    font-weight: 500;
    font-size: 0.8rem;
  }
  .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
    font-size: 0.85rem;
  }
  .check-grid {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  .check {
    display: flex;
    gap: 0.5rem;
    align-items: center;
    font-size: 0.9rem;
    cursor: pointer;
  }
  .default-pick {
    display: inline-flex;
    align-items: center;
    gap: 0.25rem;
    margin-left: 0.35rem;
    color: var(--text-muted);
    font-style: normal;
    font-size: 0.75rem;
    cursor: pointer;
  }
  .chips {
    display: flex;
    flex-wrap: wrap;
    gap: 0.25rem;
  }
  .chip {
    display: inline-block;
    padding: 0.1rem 0.45rem;
    border-radius: 999px;
    background: var(--surface-2, #1a1a22);
    border: 1px solid var(--border, #2a2a32);
    font-size: 0.75rem;
  }
  .chip-default {
    border-color: var(--accent, #3b82f6);
  }
  .muted-chip {
    color: var(--text-muted);
  }
  .status.off {
    color: var(--text-muted);
  }
  .row-actions {
    white-space: nowrap;
    text-align: right;
  }
  .linkish {
    background: none;
    border: none;
    color: var(--accent, #3b82f6);
    cursor: pointer;
    font: inherit;
    padding: 0.15rem 0.35rem;
  }
  .linkish:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
  .linkish.muted {
    color: var(--text-muted);
  }
  .linkish.danger {
    color: #f87171;
  }
  .tiny {
    font-size: 0.75rem;
  }
  .edit-row {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.35rem 0;
  }
  .edit-profiles {
    max-height: 10rem;
    overflow: auto;
    padding: 0.35rem;
    border: 1px solid var(--border, #2a2a32);
    border-radius: 0.5rem;
  }
  .edit-actions {
    display: flex;
    gap: 0.5rem;
  }
  .editing {
    background: color-mix(in srgb, var(--accent, #3b82f6) 8%, transparent);
  }
  .fw-panel {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    padding: 0.35rem 0;
  }
  .fw-upload {
    display: flex;
    align-items: center;
    gap: 0.75rem;
  }
  .small-table {
    font-size: 0.8rem;
  }
</style>
